import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicDetail({ comic }: { comic: Comic }) {
  const relatedComics = useMemo(() => {
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 6);
  }, [comic]);

  const formattedViews =
    comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views;

  return (
    <>
      <div className="mb-10">
        <Breadcrumb
          items={[
            { label: `${comic.category}`, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-8 mb-14">
        <div className="flex-shrink-0 w-full md:w-[280px]">
          <div className="aspect-[3/4] rounded-lg overflow-hidden">
            <img
              src={comic.coverImage}
              alt={comic.title}
              title={`${comic.title} - 汗汗漫画`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
            {comic.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm text-foreground-500">作者：{comic.author}</span>
            <span className="text-foreground-300">|</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                comic.status === '连载中'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-secondary-100 text-secondary-700'
              }`}
            >
              {comic.status}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="text-sm text-foreground-500">
              <i className="ri-star-fill text-primary-500 text-xs mr-1"></i>
              {comic.rating}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="text-sm text-foreground-500">
              <i className="ri-eye-line text-foreground-400 text-xs mr-1"></i>
              {formattedViews}阅读
            </span>
            <span className="text-foreground-300">|</span>
            <span className="text-sm text-foreground-500">
              共 {comic.chapters.length} 话
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {comic.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category/${comic.categorySlug}`}
                className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium hover:bg-accent-100 transition-colors whitespace-nowrap"
              >
                {tag}
              </Link>
            ))}
          </div>

          <div className="bg-background-100 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-medium text-foreground-800 mb-2">作品简介</h2>
            <p className="text-sm text-foreground-500 leading-relaxed">
              {comic.description}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-play-circle-line mr-1.5"></i>
              开始阅读
            </button>
            <button className="px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-bookmark-line mr-1.5"></i>
              收藏
            </button>
          </div>
        </div>
      </div>

      <div className="mb-14">
        <h2 className="font-heading text-xl text-foreground-900 mb-5">章节列表</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {comic.chapters.slice(0, 50).map((ch) => (
            <button
              key={ch.id}
              className="px-4 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors text-left truncate cursor-pointer whitespace-nowrap"
            >
              {ch.title}
            </button>
          ))}
        </div>
        {comic.chapters.length > 50 && (
          <button className="mt-4 px-6 py-2.5 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
            查看更多章节（共{comic.chapters.length}话）
          </button>
        )}
      </div>

      {relatedComics.length > 0 && (
        <div className="mb-14">
          <h2 className="font-heading text-xl text-foreground-900 mb-5">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Book',
            name: comic.title,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            description: comic.description,
            genre: comic.tags,
            inLanguage: 'zh-CN',
            dateModified: comic.updateDate,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: comic.rating,
              bestRating: 10,
              ratingCount: Math.floor(comic.views / 100),
            },
          }),
        }}
      />
    </>
  );
}

function NotFoundComic() {
  return (
    <div className="text-center py-20">
      <h1 className="font-heading text-2xl text-foreground-900 mb-4">漫画未找到</h1>
      <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
        返回首页
      </Link>
    </div>
  );
}

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          {comic ? <ComicDetail comic={comic} /> : <NotFoundComic />}
        </div>
      </main>
      <Footer />
    </>
  );
}